import Styled from './FlightDetailsSkeleton.styled';
import { Skeleton } from '@mui/material';
import { color, COLOR_VARIANTS, Icons, Typography, TYPOGRAPHY_VARIANT } from '@tajawal/ct-web-design-system';
import { buildTestId } from '@/utils/helpers.utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OBSERVABLE_EVENTS, TEST_ID_PREFIX } from '@/constants/common.constants';
import { Observable } from '@/utils/observer/observer';

function FlightDetailsSkeleton() {
  const testId = buildTestId(TEST_ID_PREFIX.FLIGHTS_DETAILS, 'FlightSummary', 'Wrapper');
  const { t } = useTranslation();

  const handleClose = () => {
    Observable.getInstance().notify(OBSERVABLE_EVENTS.FLIGHT_DETAILS, { unSelectAndClose: true });
  };

  return (
    <Styled.SkeletonWrapper>
      <Styled.WrapperHeader>
        <Icons.DismissIcon
          cursor="pointer"
          color={color(COLOR_VARIANTS.AQUA100)}
          width="24px"
          height="24px"
          onClick={handleClose}
          data-testid={buildTestId(testId, 'Dismiss')}
        />

        <Typography variant={TYPOGRAPHY_VARIANT.HEADING2} component="h2">
          {t('FlightDetails.FlightDetails')}
        </Typography>
      </Styled.WrapperHeader>
      <Styled.SkeletonBody>
        <Styled.SkeletonHeader>
          <div>
            <Styled.RectangleSkeleton variant="rectangular" width={130} height={28} />
            <Styled.SkeletonHeaderDuration>
              <Styled.RectangleSkeleton variant="rectangular" width={74} height={16} />
              <Styled.RectangleSkeleton variant="rectangular" width={74} height={16} />
            </Styled.SkeletonHeaderDuration>
          </div>

          <Styled.RectangleSkeleton variant="rectangular" width={122} height={16} />
        </Styled.SkeletonHeader>

        <Styled.SkeletonLegs>
          <Styled.SkeletonSection>
            <Styled.RectangleSkeleton variant="rectangular" width={64} height={20} />
            <Styled.RectangleSkeleton variant="rectangular" width={64} height={20} />
            <Styled.RectangleSkeleton variant="rectangular" width={64} height={20} />
          </Styled.SkeletonSection>
          <Styled.SkeletonSection>
            <Styled.BlueCircle />
            <Skeleton variant="circular" width={24} height={24} />
            <Styled.BlueCircle $isLastCircle />
          </Styled.SkeletonSection>
          <Styled.SkeletonSection>
            <Styled.SkeletonSectionInfo>
              <Styled.RectangleSkeleton variant="rectangular" width={280} height={20} />
              <Styled.RectangleSkeleton variant="rectangular" width={132} height={16} />
              <Styled.RectangleSkeleton variant="rectangular" width={132} height={16} />
            </Styled.SkeletonSectionInfo>

            <Styled.SkeletonAirlineInfo>
              <Skeleton variant="circular" width={40} height={40} />
              <Styled.RectangleSkeleton variant="rectangular" width={132} height={16} />
            </Styled.SkeletonAirlineInfo>

            <Styled.SkeletonSectionInfo>
              <Styled.RectangleSkeleton variant="rectangular" width={280} height={20} />
              <Styled.RectangleSkeleton variant="rectangular" width={132} height={16} />
              <Styled.RectangleSkeleton variant="rectangular" width={132} height={16} />
            </Styled.SkeletonSectionInfo>
          </Styled.SkeletonSection>
        </Styled.SkeletonLegs>

        <Styled.SkeletonBaggage>
          <Styled.BaggageContainer>
            <Styled.BaggageHeader>
              <Styled.RectangleSkeleton variant="rectangular" width={90} height={16} />
              <Styled.RectangleSkeleton variant="rectangular" width={74} height={16} />
            </Styled.BaggageHeader>
            <Styled.BaggageContent>
              <Styled.BaggageItem>
                <Skeleton variant="circular" width={18} height={18} />
                <Styled.RectangleSkeleton variant="rectangular" width={172} height={20} />
              </Styled.BaggageItem>
              <Styled.BaggageItem>
                <Skeleton variant="circular" width={18} height={18} />
                <Styled.RectangleSkeleton variant="rectangular" width={172} height={20} />
              </Styled.BaggageItem>
              <Styled.BaggageItem>
                <Skeleton variant="circular" width={18} height={18} />
                <Styled.RectangleSkeleton variant="rectangular" width={172} height={20} />
              </Styled.BaggageItem>
            </Styled.BaggageContent>
          </Styled.BaggageContainer>

          <Styled.BaggageContainer>
            <Styled.BaggageHeader>
              <Styled.RectangleSkeleton variant="rectangular" width={90} height={16} />
              <Styled.RectangleSkeleton variant="rectangular" width={74} height={16} />
            </Styled.BaggageHeader>
            <Styled.BaggageContent>
              <Styled.BaggageItem>
                <Skeleton variant="circular" width={18} height={18} />
                <Styled.RectangleSkeleton variant="rectangular" width={172} height={20} />
              </Styled.BaggageItem>
              <Styled.BaggageItem>
                <Skeleton variant="circular" width={18} height={18} />
                <Styled.RectangleSkeleton variant="rectangular" width={172} height={20} />
              </Styled.BaggageItem>
              <Styled.BaggageItem>
                <Skeleton variant="circular" width={18} height={18} />
                <Styled.RectangleSkeleton variant="rectangular" width={172} height={20} />
              </Styled.BaggageItem>
            </Styled.BaggageContent>
          </Styled.BaggageContainer>
        </Styled.SkeletonBaggage>
      </Styled.SkeletonBody>

      <Styled.SkeletonPricing>
        <Styled.SkeletonPricingTotal>
          <Styled.RectangleSkeleton variant="rectangular" width={132} height={16} />
          <Styled.RectangleSkeleton variant="rectangular" width={132} height={28} />
        </Styled.SkeletonPricingTotal>

        <Styled.ButtonSkeleton variant="rectangular" width={144} height={48} />
      </Styled.SkeletonPricing>
    </Styled.SkeletonWrapper>
  );
}

export default FlightDetailsSkeleton;
